import { createSlice } from "@reduxjs/toolkit";
import {
  loadCapabilities,
  loadUserContext,
  evaluatePermission,
  evaluateBatch,
  getPermittedActions,
} from "./actions";

const initialState = {
  // User context data
  userContext: null,

  // User capabilities (all permitted actions + flags)
  capabilities: null,

  // Permission cache (resource-based)
  permissionCache: {},

  // Permitted actions by resource type
  permittedActions: {},

  // Organization hierarchy data
  hierarchy: null,

  // Loading states
  loading: {
    capabilities: false,
    userContext: false,
    permission: false,
    batch: false,
    permittedActions: {},
    hierarchy: false,
  },

  // Error states
  error: {
    capabilities: null,
    userContext: null,
    permission: null,
    batch: null,
    permittedActions: {},
    hierarchy: null,
  },

  // Performance metrics
  metrics: {
    cacheHits: 0,
    cacheMisses: 0,
    evaluations: 0,
    batchEvaluations: 0,
    totalRequests: 0,
  },
};

const abacSlice = createSlice({
  name: "abac",
  initialState,
  reducers: {
    // Manual cache invalidation
    invalidateCache: (state, action) => {
      const { pattern } = action.payload || {};

      if (!pattern || pattern === "*") {
        // Clear all cache
        state.permissionCache = {};
        state.permittedActions = {};
      } else {
        // Clear cache matching pattern
        const regex = new RegExp(
          pattern.replace(/\*/g, ".*").replace(/:/g, "\\:")
        );

        // Clear permission cache
        Object.keys(state.permissionCache).forEach((key) => {
          if (regex.test(key)) {
            delete state.permissionCache[key];
          }
        });

        // Clear permitted actions cache
        Object.keys(state.permittedActions).forEach((key) => {
          if (regex.test(key)) {
            delete state.permittedActions[key];
          }
        });
      }
    },

    // Clear all cache
    clearCache: (state) => {
      state.permissionCache = {};
      state.permittedActions = {};
    },

    // Update metrics
    updateMetrics: (state, action) => {
      const { type, value = 1 } = action.payload;
      if (state.metrics[type] !== undefined) {
        state.metrics[type] += value;
      }
    },

    // Reset metrics
    resetMetrics: (state) => {
      state.metrics = {
        cacheHits: 0,
        cacheMisses: 0,
        evaluations: 0,
        batchEvaluations: 0,
        totalRequests: 0,
      };
    },

    // Set hierarchy data
    setHierarchy: (state, action) => {
      state.hierarchy = action.payload;
    },
  },

  extraReducers: (builder) => {
    // Load Capabilities
    builder
      .addCase(loadCapabilities.pending, (state) => {
        state.loading.capabilities = true;
        state.error.capabilities = null;
      })
      .addCase(loadCapabilities.fulfilled, (state, action) => {
        state.loading.capabilities = false;
        state.capabilities = {
          ...action.payload,
          timestamp: Date.now(),
        };
        state.error.capabilities = null;

        // Also populate permissionCache from capabilities for compatibility
        const actions = action.payload.actions || [];
        actions.forEach((actionStr) => {
          // actionStr format: "resource.action" (e.g., "asset.view")
          const [resourceType, actionName] = actionStr.split(".");
          if (resourceType && actionName) {
            const cacheKey = `${resourceType}:${actionName}`;
            state.permissionCache[cacheKey] = {
              granted: true,
              timestamp: Date.now(),
            };
          }
        });
      })
      .addCase(loadCapabilities.rejected, (state, action) => {
        state.loading.capabilities = false;
        state.error.capabilities = action.payload || action.error.message;
      });

    // Load User Context
    builder
      .addCase(loadUserContext.pending, (state) => {
        state.loading.userContext = true;
        state.error.userContext = null;
      })
      .addCase(loadUserContext.fulfilled, (state, action) => {
        state.loading.userContext = false;
        state.userContext = action.payload;
        state.error.userContext = null;
      })
      .addCase(loadUserContext.rejected, (state, action) => {
        state.loading.userContext = false;
        state.error.userContext = action.payload || action.error.message;
      });

    // Evaluate Permission
    builder
      .addCase(evaluatePermission.pending, (state) => {
        state.loading.permission = true;
        state.error.permission = null;
        state.metrics.evaluations += 1;
        state.metrics.totalRequests += 1;
      })
      .addCase(evaluatePermission.fulfilled, (state, action) => {
        state.loading.permission = false;
        state.error.permission = null;

        const { result, cacheKey, fromCache } = action.payload;

        // Update cache - extract permission data from API response wrapper
        // API returns { success, data: { granted } } but selector expects { granted }
        const permissionData = result?.data || result;
        state.permissionCache[cacheKey] = {
          ...permissionData,
          timestamp: Date.now(),
        };

        // Update metrics
        if (fromCache) {
          state.metrics.cacheHits += 1;
        } else {
          state.metrics.cacheMisses += 1;
        }
      })
      .addCase(evaluatePermission.rejected, (state, action) => {
        state.loading.permission = false;
        state.error.permission = action.payload || action.error.message;
      });

    // Evaluate Batch
    builder
      .addCase(evaluateBatch.pending, (state) => {
        state.loading.batch = true;
        state.error.batch = null;
        state.metrics.batchEvaluations += 1;
        state.metrics.totalRequests += 1;
      })
      .addCase(evaluateBatch.fulfilled, (state, action) => {
        state.loading.batch = false;
        state.error.batch = null;

        const { results, permissions } = action.payload;

        // Update cache for each result (with safety check)
        if (Array.isArray(results)) {
          results.forEach((result, index) => {
            const permission = permissions[index];
            const cacheKey = `${permission.resourceType}:${permission.action}${
              permission.resourceId ? `:${permission.resourceId}` : ""
            }`;

            state.permissionCache[cacheKey] = {
              ...result,
              timestamp: Date.now(),
            };
          });

          state.metrics.evaluations += results.length;
        }
      })
      .addCase(evaluateBatch.rejected, (state, action) => {
        state.loading.batch = false;
        state.error.batch = action.payload || action.error.message;
      });

    // Get Permitted Actions
    builder
      .addCase(getPermittedActions.pending, (state, action) => {
        const resourceType = action.meta.arg;
        state.loading.permittedActions[resourceType] = true;
        state.error.permittedActions[resourceType] = null;
      })
      .addCase(getPermittedActions.fulfilled, (state, action) => {
        const { resourceType, actions } = action.payload;
        state.loading.permittedActions[resourceType] = false;
        state.permittedActions[resourceType] = {
          actions,
          timestamp: Date.now(),
        };
        state.error.permittedActions[resourceType] = null;
      })
      .addCase(getPermittedActions.rejected, (state, action) => {
        const resourceType = action.meta.arg;
        state.loading.permittedActions[resourceType] = false;
        state.error.permittedActions[resourceType] =
          action.payload || action.error.message;
      });
  },
});

export const {
  invalidateCache,
  clearCache,
  updateMetrics,
  resetMetrics,
  setHierarchy,
} = abacSlice.actions;

export default abacSlice.reducer;
